import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, Plus, RefreshCw, XCircle } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const formatRupiah = (amount) => {
  const numericAmount = Number(amount || 0);
  const hasFraction = Math.round(Math.abs(numericAmount % 1) * 100) > 0;
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(numericAmount);
};

const sanitizeCurrencyInput = (raw) => String(raw || "").replace(/[^0-9.,]/g, "");

const parseCurrencyAmount = (raw) => {
  const value = sanitizeCurrencyInput(raw);
  if (!value) return 0;

  const lastComma = value.lastIndexOf(",");
  const lastDot = value.lastIndexOf(".");
  const hasComma = lastComma > -1;
  const hasDot = lastDot > -1;
  const decimalSeparator = lastComma > lastDot ? "," : lastDot > -1 ? "." : "";

  if (!decimalSeparator) {
    return Number(value.replace(/\D/g, "") || 0);
  }

  const separatorIndex = decimalSeparator === "," ? lastComma : lastDot;
  const integerPart = value.slice(0, separatorIndex).replace(/\D/g, "");
  const decimalPart = value.slice(separatorIndex + 1).replace(/\D/g, "");
  if (decimalPart.length === 3 && hasComma !== hasDot) {
    return Number(value.replace(/\D/g, "") || 0);
  }
  return Number(`${integerPart || "0"}.${decimalPart || "0"}`);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusClass = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "APPROVED") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
  if (normalized === "REJECTED") return "border-red-500/30 bg-red-500/10 text-red-300";
  return "border-amber-500/30 bg-amber-500/10 text-amber-300";
};

const getRequestTypeMeta = (requestType) => {
  const normalized = String(requestType || "").toUpperCase();
  if (normalized === "CREATE_PRODUCT") {
    return {
      label: "Tambah Produk",
      detailLabel: "Stok",
      detailValueLabel: "Request Dana",
      pricingLabel: "Harga Modal",
      quantityPrefix: "+",
    };
  }
  if (normalized === "CONSIGNMENT_SETTLEMENT") {
    return {
      label: "Rekap Setoran",
      detailLabel: "Stok Titipan",
      detailValueLabel: "Nilai Setoran",
      pricingLabel: "Harga Beli",
      quantityPrefix: "Terjual ",
    };
  }
  if (normalized === "GENERAL") {
    return {
      label: "Request Umum",
      detailLabel: "Jenis",
      detailValueLabel: "Nominal",
      pricingLabel: "Bukti",
      quantityPrefix: "",
    };
  }
  return {
    label: "Tambah Stok",
    detailLabel: "Stok",
    detailValueLabel: "Request Dana",
    pricingLabel: "Harga Modal",
    quantityPrefix: "+",
  };
};

export default function ProductPaymentRequests() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const isKasir = String(user?.role || "").toLowerCase() === "kasir";
  const currentMerchantId = String(user?.merchant_id || "").trim();
  const [requests, setRequests] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState("");
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [requestForm, setRequestForm] = useState({
    request_type: "GENERAL",
    title: "",
    product_id: "",
    quantity: "",
    cost_amount: "",
    notes: "",
  });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/product-payment-requests`, { withCredentials: true });
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch product payment requests:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data payment request.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!isKasir) return;
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      const productList = (Array.isArray(data) ? data : []).filter(
        (item) =>
          item &&
          item.is_active !== false &&
          String(item.sale_type || "OWN").toUpperCase() !== "CONSIGNMENT" &&
          String(item.merchant_id || "").trim() === currentMerchantId
      );
      setProducts(productList);
      if (productList.length > 0) {
        setRequestForm((prev) => ({ ...prev, product_id: productList[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch products for payment request:", error);
      setProducts([]);
    }
  }, [currentMerchantId, isKasir]);

  useEffect(() => {
    fetchRequests();
    fetchProducts();
  }, [fetchProducts, fetchRequests]);

  const selectedProduct = useMemo(
    () => products.find((item) => item.id === requestForm.product_id) || null,
    [products, requestForm.product_id]
  );
  const isStockRequest = requestForm.request_type === "ADD_STOCK";

  const summary = useMemo(() => {
    const pending = requests.filter((item) => String(item.status || "").toUpperCase() === "PENDING");
    const approved = requests.filter((item) => String(item.status || "").toUpperCase() === "APPROVED");
    const rejected = requests.filter((item) => String(item.status || "").toUpperCase() === "REJECTED");
    return {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, item) => sum + Number(item.cost_amount || 0), 0),
      approvedCount: approved.length,
      rejectedCount: rejected.length,
    };
  }, [requests]);

  const updateRequestStatus = async (request, action) => {
    const id = String(request?.id || "").trim();
    if (!id) return;
    const label = action === "approve" ? "approve" : "reject";
    const requestTypeMeta = getRequestTypeMeta(request?.request_type);
    if (!window.confirm(`${label} payment request ${requestTypeMeta.label.toLowerCase()} ${request.product_name || request.title || id}?`)) return;
    try {
      setProcessingId(id);
      await axios.post(`${API}/product-payment-requests/${id}/${action}`, {}, { withCredentials: true });
      await fetchRequests();
    } catch (error) {
      console.error(`Failed to ${action} product payment request:`, error);
      alert(error.response?.data?.detail || `Gagal ${label} payment request.`);
    } finally {
      setProcessingId("");
    }
  };

  const handleCreatePaymentRequest = async (event) => {
    event.preventDefault();
    if (!isKasir) return;
    if (isStockRequest && !requestForm.product_id) {
      alert("Pilih produk terlebih dulu.");
      return;
    }
    const quantity = Number(requestForm.quantity || 0);
    const costAmount = parseCurrencyAmount(requestForm.cost_amount);
    if (isStockRequest && (!quantity || quantity <= 0)) {
      alert("Jumlah stok wajib lebih dari 0.");
      return;
    }
    if (!isStockRequest && !requestForm.title.trim()) {
      alert("Judul request wajib diisi.");
      return;
    }
    if (!costAmount || costAmount <= 0) {
      alert("Nominal payment request wajib lebih dari 0.");
      return;
    }
    try {
      setCreatingRequest(true);
      let evidencePath = "";
      if (evidenceFile) {
        const uploadData = new FormData();
        uploadData.append("file", evidenceFile);
        const uploadRes = await axios.post(`${API}/uploads/payment-request-evidence`, uploadData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        evidencePath = uploadRes.data?.path || "";
      }
      await axios.post(
        `${API}/product-payment-requests`,
        {
          request_type: requestForm.request_type,
          title: isStockRequest ? `Tambah Stok ${selectedProduct?.name || "Produk"}` : requestForm.title.trim(),
          product_id: isStockRequest ? requestForm.product_id : "",
          quantity: isStockRequest ? quantity : 0,
          cost_amount: costAmount,
          notes: requestForm.notes.trim(),
          evidence_path: evidencePath,
        },
        { withCredentials: true }
      );
      alert("Payment request berhasil dikirim ke admin.");
      setRequestForm({
        request_type: "GENERAL",
        title: "",
        product_id: products[0]?.id || "",
        quantity: "",
        cost_amount: "",
        notes: "",
      });
      setEvidenceFile(null);
      setIsCreateOpen(false);
      await fetchRequests();
    } catch (error) {
      console.error("Failed to create payment request:", error);
      alert(error.response?.data?.detail || "Gagal membuat payment request.");
    } finally {
      setCreatingRequest(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title="Payment Request" />
      <div className="space-y-5 p-6 lg:p-8" data-testid="product-payment-requests-page">
        <section className="flex flex-col gap-4 rounded-sm border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.025),rgba(255,255,255,0))] p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3D77A] hover:bg-[#D4AF37]/10">
              Approval Kasir
            </Badge>
            <h2 className="text-2xl font-semibold text-white">Payment Request</h2>
            <p className="mt-1 text-sm text-zinc-400">
              {isAdmin
                ? "Approve request pembayaran umum, stok produk, dan rekap setoran titipan dari kasir."
                : "Buat request pembayaran umum atau tambah stok produk untuk approval admin."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isKasir && (
              <Button
                type="button"
                onClick={() => setIsCreateOpen((value) => !value)}
                className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
              >
                <Plus className="mr-2 h-4 w-4" />
                Tambah
              </Button>
            )}
            <Button type="button" onClick={fetchRequests} className="rounded-sm border border-white/20 bg-white/[0.03] text-white hover:bg-white/[0.08]">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </section>

        {isKasir && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-sm border-[#D4AF37]/20 bg-zinc-950 p-0 text-white shadow-2xl shadow-black/50">
              <DialogHeader className="border-b border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_34%)] px-5 py-5 pr-12 text-left sm:px-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37]">Payment Request</p>
                <DialogTitle className="mt-2 text-xl font-semibold text-white">Buat Request Baru</DialogTitle>
                <p className="mt-1 text-sm text-zinc-400">
                  Isi data request, lalu admin akan melakukan approve atau reject.
                </p>
              </DialogHeader>

            <form onSubmit={handleCreatePaymentRequest} className="space-y-4 p-5 sm:p-6">
              <div className="rounded-sm border border-white/10 bg-white/[0.025] p-4">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">1. Pilih Jenis Request</p>
                    <p className="mt-1 text-xs text-zinc-500">Pilih apakah request ini untuk pembayaran umum atau tambah stok produk.</p>
                  </div>
                  <Badge className="rounded-sm border border-white/10 bg-white/[0.04] text-zinc-300 hover:bg-white/[0.04]">
                    {isStockRequest ? "Stok Produk" : "Umum"}
                  </Badge>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setRequestForm((prev) => ({ ...prev, request_type: "GENERAL" }))}
                    className={`rounded-sm border p-4 text-left transition ${!isStockRequest ? "border-[#D4AF37]/60 bg-[#D4AF37]/10" : "border-white/10 bg-black/20 hover:bg-white/[0.04]"}`}
                  >
                    <p className="font-semibold text-white">Request Umum</p>
                    <p className="mt-1 text-xs text-zinc-500">Untuk vendor, operasional, refund, atau pembayaran lain.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestForm((prev) => ({ ...prev, request_type: "ADD_STOCK" }))}
                    className={`rounded-sm border p-4 text-left transition ${isStockRequest ? "border-[#D4AF37]/60 bg-[#D4AF37]/10" : "border-white/10 bg-black/20 hover:bg-white/[0.04]"}`}
                  >
                    <p className="font-semibold text-white">Tambah Stok Produk</p>
                    <p className="mt-1 text-xs text-zinc-500">Stok produk bertambah setelah admin approve.</p>
                  </button>
                </div>
              </div>

              <div className="rounded-sm border border-white/10 bg-white/[0.025] p-4">
                <div className="mb-4">
                  <p className="text-sm font-semibold text-white">2. Detail Request</p>
                  <p className="mt-1 text-xs text-zinc-500">{isStockRequest ? "Pilih produk dan masukkan jumlah stok yang ingin ditambahkan." : "Isi judul request dan lampirkan bukti/invoice jika ada."}</p>
                </div>
                {isStockRequest ? (
                  <div className="grid gap-4 sm:grid-cols-[1.4fr_.6fr]">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Produk</Label>
                    <select
                      value={requestForm.product_id}
                      onChange={(event) => setRequestForm((prev) => ({ ...prev, product_id: event.target.value }))}
                      className="mt-2 h-12 w-full rounded-sm border-0 bg-white/[0.06] px-4 font-semibold text-white outline-none ring-1 ring-white/[0.08] transition focus:ring-[#D4AF37]/50 [&_option]:bg-white [&_option]:text-black"
                      required
                    >
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} - stok {Number(product.stock || 0)}
                        </option>
                      ))}
                    </select>
                    </div>
                    <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">Stok Sekarang</p>
                      <p className="mt-2 text-2xl font-semibold text-white">{selectedProduct ? Number(selectedProduct.stock || 0) : 0}</p>
                      <p className="text-xs text-zinc-500">pcs</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Judul Request</Label>
                    <Input
                      value={requestForm.title}
                      onChange={(event) => setRequestForm((prev) => ({ ...prev, title: event.target.value }))}
                      className="h-12 rounded-sm border-white/10 bg-white/[0.06] text-white focus-visible:ring-[#D4AF37]/50"
                      placeholder="Contoh: Bayar vendor air galon"
                      required={!isStockRequest}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Bukti / Invoice</Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(event) => setEvidenceFile(event.target.files?.[0] || null)}
                      className="h-12 rounded-sm border-white/10 bg-white/[0.06] pt-3 text-white file:mr-3 file:rounded-sm file:border-0 file:bg-[#D4AF37] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-black focus-visible:ring-[#D4AF37]/50"
                    />
                    {evidenceFile ? <p className="text-xs text-zinc-500">{evidenceFile.name}</p> : null}
                  </div>
                </div>
                )}
              </div>

              <div className="rounded-sm border border-white/10 bg-white/[0.025] p-4">
                <div className="mb-4">
                  <p className="text-sm font-semibold text-white">3. Nominal dan Catatan</p>
                  <p className="mt-1 text-xs text-zinc-500">Masukkan nominal request dan catatan tambahan bila diperlukan.</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {isStockRequest && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Jumlah Stok</Label>
                      <Input
                        type="number"
                        min={1}
                        value={requestForm.quantity}
                        onChange={(event) => setRequestForm((prev) => ({ ...prev, quantity: event.target.value }))}
                        className="h-12 rounded-sm border-white/10 bg-white/[0.06] text-white focus-visible:ring-[#D4AF37]/50"
                        placeholder="Contoh: 10"
                        required
                      />
                    </div>
                  )}
                  {isStockRequest && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Bukti Foto / Invoice</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(event) => setEvidenceFile(event.target.files?.[0] || null)}
                        className="h-12 rounded-sm border-white/10 bg-white/[0.06] pt-3 text-white file:mr-3 file:rounded-sm file:border-0 file:bg-[#D4AF37] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-black focus-visible:ring-[#D4AF37]/50"
                      />
                      {evidenceFile ? <p className="text-xs text-zinc-500">{evidenceFile.name}</p> : null}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Nominal Request</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={requestForm.cost_amount}
                    onChange={(event) => setRequestForm((prev) => ({ ...prev, cost_amount: sanitizeCurrencyInput(event.target.value) }))}
                    className="h-12 rounded-sm border-white/10 bg-white/[0.06] text-white focus-visible:ring-[#D4AF37]/50"
                    placeholder="Contoh: 469.999,20"
                    required
                  />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Catatan</Label>
                    <Textarea
                      rows={3}
                      value={requestForm.notes}
                      onChange={(event) => setRequestForm((prev) => ({ ...prev, notes: event.target.value }))}
                      className="min-h-24 resize-none rounded-sm border-white/10 bg-white/[0.06] text-white focus-visible:ring-[#D4AF37]/50"
                      placeholder={isStockRequest ? "Opsional, contoh: restock untuk promo minggu ini" : "Opsional, contoh: pembayaran invoice vendor bulan ini"}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/10 p-4">
                <p className="text-sm font-semibold text-[#F3D77A]">Status: Menunggu approve admin</p>
                <p className="mt-1 text-xs text-zinc-400">
                  {isStockRequest ? "Jika disetujui, stok produk akan otomatis bertambah." : "Jika disetujui, request umum hanya berubah status menjadi approved."}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  type="submit"
                  disabled={creatingRequest || (isStockRequest && products.length === 0)}
                  className="h-12 rounded-sm bg-[#D4AF37] font-semibold text-black shadow-lg shadow-[#D4AF37]/10 hover:bg-[#C59A27] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creatingRequest ? "Mengirim Request..." : "Kirim Payment Request"}
                </Button>
                <Button
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  disabled={creatingRequest}
                  className="h-12 rounded-sm border border-white/10 bg-white/[0.03] font-semibold text-white hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Batal
                </Button>
              </div>
            </form>
            </DialogContent>
          </Dialog>
        )}

        <section className="grid gap-3 md:grid-cols-4">
          <div className="rounded-sm border border-amber-500/25 bg-amber-500/10 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300/80">Pending</p>
            <p className="mt-2 text-2xl font-semibold text-amber-200">{summary.pendingCount}</p>
          </div>
          <div className="rounded-sm border border-sky-500/20 bg-sky-500/10 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/80">Nominal Pending</p>
            <p className="mt-2 text-xl font-semibold text-sky-200">{formatRupiah(summary.pendingAmount)}</p>
          </div>
          <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/10 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300/80">Approved</p>
            <p className="mt-2 text-2xl font-semibold text-emerald-200">{summary.approvedCount}</p>
          </div>
          <div className="rounded-sm border border-red-500/20 bg-red-500/10 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-red-300/80">Rejected</p>
            <p className="mt-2 text-2xl font-semibold text-red-200">{summary.rejectedCount}</p>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="border-b border-white/10 bg-white/[0.02] px-5 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Daftar Request</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
            </div>
          ) : requests.length > 0 ? (
            <div className="max-h-[620px] divide-y divide-white/5 overflow-auto">
              {requests.map((request) => {
                const status = String(request.status || "PENDING").toUpperCase();
                const isPending = status === "PENDING";
                const requestTypeMeta = getRequestTypeMeta(request.request_type);
                const isGeneralRequest = String(request.request_type || "").toUpperCase() === "GENERAL";
                return (
                  <div key={request.id} className="grid gap-4 px-5 py-4 text-sm hover:bg-white/[0.02] lg:grid-cols-[1.25fr_.75fr_.75fr_.9fr_auto] lg:items-center">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate font-semibold text-white">{request.product_name || request.title || "-"}</p>
                        <span className="rounded-sm border border-sky-500/25 bg-sky-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-sky-300">
                          {requestTypeMeta.label}
                        </span>
                        <span className={`rounded-sm border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${statusClass(status)}`}>
                          {status}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-zinc-500">{request.created_by || "-"} {request.merchant_name ? `- ${request.merchant_name}` : ""}</p>
                      <p className="mt-1 text-xs text-zinc-600">{formatDateTime(request.created_at)}</p>
                      {request.notes ? <p className="mt-1 truncate text-xs text-zinc-500">{request.notes}</p> : null}
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600">{requestTypeMeta.detailLabel}</p>
                      {isGeneralRequest ? (
                        <>
                          <p className="mt-1 text-zinc-200">Umum</p>
                          <p className="text-xs text-[#F3D77A]">Approval pembayaran</p>
                        </>
                      ) : (
                        <>
                          <p className="mt-1 text-zinc-200">{request.stock_before} -&gt; {request.stock_after}</p>
                          <p className="text-xs text-[#F3D77A]">{requestTypeMeta.quantityPrefix}{request.quantity} pcs</p>
                        </>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600">{requestTypeMeta.detailValueLabel}</p>
                      <p className="mt-1 font-semibold text-sky-300">{formatRupiah(request.cost_amount)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600">{requestTypeMeta.pricingLabel}</p>
                      {isGeneralRequest ? (
                        <p className="mt-1 text-zinc-500">{request.evidence_path ? "Terlampir" : "Tidak ada"}</p>
                      ) : (
                        <p className="mt-1 text-zinc-300">{formatRupiah(request.cost_price_after)}</p>
                      )}
                      {request.evidence_path ? (
                          <a
                            href={`${BACKEND_URL}${request.evidence_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex rounded-sm border border-white/10 px-3 py-1.5 text-xs font-semibold text-[#F3D77A] hover:bg-white/[0.05]"
                          >
                            Lihat Bukti
                          </a>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      {isAdmin && isPending ? (
                        <>
                          <Button
                            type="button"
                            onClick={() => updateRequestStatus(request, "approve")}
                            disabled={processingId === request.id}
                            className="h-9 rounded-sm bg-emerald-500/15 px-3 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/20 disabled:cursor-wait disabled:opacity-60"
                          >
                            <CheckCircle2 className="mr-1.5 h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            type="button"
                            onClick={() => updateRequestStatus(request, "reject")}
                            disabled={processingId === request.id}
                            className="h-9 rounded-sm bg-red-500/15 px-3 text-xs font-semibold text-red-300 ring-1 ring-red-500/30 hover:bg-red-500/20 disabled:cursor-wait disabled:opacity-60"
                          >
                            <XCircle className="mr-1.5 h-4 w-4" />
                            Reject
                          </Button>
                        </>
                      ) : (
                        <span className="rounded-sm border border-white/10 px-3 py-2 text-xs text-zinc-500">Riwayat</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center text-sm text-zinc-500">Belum ada payment request.</div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
